import { isWeb, useListPaymentMethodsQuery } from "@stepapp/core";
import {
  Block,
  BottomSheet,
  Button,
  palette,
  SvgIcon,
  spacing,
  Touchable,
  Typography,
  useBreakpoints,
} from "@stepapp/ui";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { useAppSelector } from "../../hooks";
import { AddCard } from "../Payment/AddCard";
import { PaymentMethods } from "../Payment/PaymentMethods";
import PaymentOption from "../Payment/PaymentOption";
import ServiceNotice from "../ServiceNotice";

type PaymentProps = {
  paymentMethodId: string;
  selectPaymentMethod: (paymentMethodId: string) => void;
  handleCheckout: () => void;
  disabledSubmit: boolean;
  loadingSubmit: boolean;
};

export const ServiceCheckout = ({
  paymentMethodId,
  selectPaymentMethod,
  handleCheckout,
  disabledSubmit,
  loadingSubmit,
}: PaymentProps) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { slug } = useLocalSearchParams();
  const [addNewCard, setAddNewCard] = useState(false);
  const [sheetVisible, setSheetVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const { md } = useBreakpoints();
  const isLogged = useAppSelector(({ user }) => user.id);

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(paymentMethodId);

  const { data } = useListPaymentMethodsQuery({
    skip: !isLogged,
    onCompleted(data) {
      selectPaymentMethod(data?.listPaymentMethods[0]?.id || "");
    },
  });

  const card = useMemo(
    () => data?.listPaymentMethods?.find((pm) => pm.id === paymentMethodId),
    [data?.listPaymentMethods, paymentMethodId],
  );

  const addFirstCard = !card?.id && isWeb;

  const toggleSheetVisibility = useCallback(
    (value?: boolean) => {
      setSheetVisible((v) => (typeof value === "boolean" ? value : !v));
      setSelectedPaymentMethodId(paymentMethodId);
    },
    [paymentMethodId],
  );

  const handleOpenPaymentModal = useCallback(() => {
    if (isWeb) {
      return setSheetVisible(true);
    }

    navigate(`/service/${slug}/payment-method`);
  }, [slug, navigate]);

  const handleChangePayment = useCallback(() => {
    selectPaymentMethod(selectedPaymentMethodId);
    setSheetVisible(false);
    setAddNewCard(false);
  }, [selectPaymentMethod, selectedPaymentMethodId]);

  const handleClose = useCallback(
    (id: string) => {
      selectPaymentMethod(id);
      setSheetVisible(false);
      setAddNewCard(false);
    },
    [selectPaymentMethod],
  );

  useEffect(() => {
    setSelectedPaymentMethodId(paymentMethodId);
  }, [paymentMethodId]);

  const handleToggleBottomSheet = useCallback(() => {
    toggleSheetVisibility(false);
  }, [toggleSheetVisibility]);

  const handleClickBackIcon = useCallback(() => {
    setAddNewCard(false);
  }, []);

  return (
    <Block paddingTop={1}>
      <Typography variant="sectionTitle">
        {t("screens.serviceSummary.choosePaymentMethod")}
      </Typography>

      <Block marginVertical={3}>
        <Typography variant="body">
          {addFirstCard
            ? t("pages.serviceSummary.addCard")
            : t("screens.serviceSummary.cardPayment")}
        </Typography>
        <Typography type="secondary" variant="lightLabel">
          {t("screens.serviceSummary.paymentNotice")}
        </Typography>
      </Block>

      {addFirstCard ? (
        <>
          <AddCard onComplete={selectPaymentMethod} loading={loading} setLoading={setLoading} />
          <Typography type="secondary" variant="lightLabel" style={styles.linkCardTypography}>
            {t("screens.serviceSummary.yourCardWillBeLinkedTo")}
          </Typography>
        </>
      ) : (
        <>
          <PaymentOption
            icon="edit"
            label={card?.last4}
            onPress={handleOpenPaymentModal}
            type={card ? (card.brand as "visa") : "add"}
          />
          {isWeb && (
            <Button
              disabled={disabledSubmit}
              loading={loadingSubmit}
              style={styles.submitBtn}
              label={t("screens.serviceSummary.confirmAndOrder")}
              onPress={handleCheckout}
              type="primary"
            />
          )}
        </>
      )}

      <ServiceNotice type="payment" containerStyles={styles.paymentInfo} />

      <BottomSheet
        avoidKeyboard
        contentStyle={md ? styles.bottomSheetWeb : styles.bottomSheetMobile}
        toggle={handleToggleBottomSheet}
        visible={sheetVisible}
      >
        <Block row alignCenter>
          {addNewCard && (
            <Touchable onPress={handleClickBackIcon}>
              <Block alignCenter justifyCenter style={styles.backIcon}>
                <SvgIcon color={palette.common.black} name="chevron-left" size={24} />
              </Block>
            </Touchable>
          )}
          <Typography variant="sectionTitle">
            {addNewCard ? t("pages.chooseCard.newCard") : t("pages.chooseCard.chooseCard")}
          </Typography>
        </Block>

        {addNewCard ? (
          <Block marginTop={4}>
            <AddCard
              showPaymentNotice
              loading={loading}
              setLoading={setLoading}
              onComplete={handleClose}
            />
          </Block>
        ) : (
          <>
            <Block marginTop={1} marginBottom={4}>
              <Typography type="secondary" variant="lightLabel">
                {t("screens.serviceSummary.paymentNotice")}
              </Typography>
            </Block>

            <PaymentMethods
              paymentMethodId={selectedPaymentMethodId}
              selectPaymentMethod={setSelectedPaymentMethodId}
              setLoading={setLoading}
            />

            <Block row marginBottom={3} marginTop={0.5}>
              <Button
                containerStyle={styles.addNewCardBtn}
                disabled={loading}
                label={t("pages.chooseCard.newCard")}
                onPress={() => setAddNewCard(true)}
                type="secondary"
                variant="small"
              />
            </Block>

            <Button
              label={t("pages.chooseCard.confirm")}
              onPress={handleChangePayment}
              type="primary"
            />
          </>
        )}
      </BottomSheet>
    </Block>
  );
};

const styles = StyleSheet.create({
  addNewCardBtn: {
    paddingHorizontal: spacing.unit * 4,
  },
  backIcon: {
    backgroundColor: palette.secondary.grey,
    borderRadius: spacing.unit * 2.5,
    height: spacing.unit * 4,
    width: spacing.unit * 4,
    marginRight: spacing.unit * 1.5,
  },
  bottomSheetMobile: {
    minWidth: spacing.unit * 50,
    padding: spacing.unit * 2.5,
    paddingBottom: spacing.unit * 4,
    maxHeight: spacing.deviceHeight * 0.9,
  },
  bottomSheetWeb: {
    minWidth: spacing.unit * 80,
    padding: spacing.unit * 4,
    paddingBottom: spacing.unit * 4,
    maxHeight: spacing.deviceHeight * 0.9,
  },
  cardPayment: {
    marginTop: spacing.unit * 3,
  },
  linkCardTypography: {
    marginTop: spacing.unit * 3,
  },
  paymentInfo: {
    marginTop: spacing.unit * 2,
  },
  submitBtn: {
    marginVertical: spacing.unit * 2,
  },
});
